/**
 * Marketing-facing "family groups" — what the public /catalog page
 * collapses the underlying admin line types into. Order intentionally
 * leads with the custom-made family (highest brand value), then
 * ready-made, then materials, then footwear.
 *
 * The customer app's wizard uses the raw `line_type` values directly,
 * so this grouping is intentionally marketing-only.
 */

export type FamilyGroupKey =
  | 'custom-made-solutions'
  | 'ready-made-insoles'
  | 'shells'
  | 'top-covers'
  | 'bottom-covers'
  | 'modification-accessories'
  | 'eva-blocks'
  | 'other-materials'
  | 'footwear';

export interface FamilyGroup {
  key: FamilyGroupKey;
  /** Display label shown on the family tile + section header. */
  label: string;
  /** One-line tagline for the section header. */
  tagline: string;
  /** Slightly longer description shown under the section header on
   *  the catalog page. */
  description: string;
  /** Admin line_type values that belong to this group. */
  lineTypes: readonly string[];
  /** Crossfading hero images for the family tile in the "Browse by
   *  category" overview. 3-4 images per family, all relative to
   *  /products/ in the marketing public folder. The first image is
   *  used as the static fallback when JS hasn't hydrated yet. */
  tileImages: readonly string[];
  /** When true, the card + product page render in custom-made mode:
   *  no "Enquire" button on the card; the product page surfaces a
   *  delivery-time line and a "Find a clinic" CTA instead. */
  isCustomMade?: boolean;
}

export const FAMILY_GROUPS: readonly FamilyGroup[] = [
  {
    key: 'custom-made-solutions',
    label: 'Custom-made solutions',
    tagline: 'Insoles and sandals built per patient scan.',
    description:
      'Carbon, polypropylene, fibreglass, EVA, PA12 and TPU substrates, chosen by the prescriber. Custom orthopaedic sandals built around a custom EVA footbed. Dispensed through partner clinics with a 5-14 working day turnaround.',
    lineTypes: ['Custom-Made Solutions'],
    tileImages: [
      'custom-made-insoles/sdl.png',
      'custom-made-insoles/cns-cf.jpg',
      'custom-made-insoles/cns-pa.png',
      'custom-made-insoles/cns-eva.jpg',
    ],
    isCustomMade: true,
  },
  {
    key: 'ready-made-insoles',
    label: 'Ready-made insoles',
    tagline: 'Standardised insoles, in stock and ready to dispense.',
    description:
      'Five clinical insole models in fibreglass and carbon shells, sized adult and pediatric. For plantar fasciitis, heel pain, pronation control and growing feet.',
    lineTypes: ['Ready-Made Insoles'],
    tileImages: [
      'ready-made/CAPE.png',
      'ready-made/CA2E.png',
      'ready-made/DAHE.png',
      'ready-made/KIK2.png',
    ],
  },
  {
    key: 'shells',
    label: 'Shells',
    tagline: 'Rigid bases for fully custom orthotic builds.',
    description:
      'Pre-formed and flat shells in fibreglass and carbon. Heat-moldable, lightweight, and engineered as the base layer for every custom insole.',
    lineTypes: ['Shells'],
    tileImages: [
      'orthopaedic-materials/SH-007.jpg',
      'orthopaedic-materials/SH-009.jpg',
      'orthopaedic-materials/SH-005.jpg',
      'orthopaedic-materials/SH-008.jpg',
    ],
  },
  {
    key: 'top-covers',
    label: 'Top covers',
    tagline: 'Comfort, cushioning and finish, single and dual-density.',
    description:
      'Sheet stock and precut pairs across Combined, EVA, PORON/PUR, and DryFeet families. Choose by patient need, footwear type, and clinical indication.',
    lineTypes: [
      'Combined Top Cover',
      'EVA Top Cover',
      'PORON/PUR Top Cover',
      'DryFeet Top Cover',
    ],
    tileImages: [
      'orthopaedic-materials/CC-005.png',
      'orthopaedic-materials/ET-001.png',
      'orthopaedic-materials/PU-001.png',
      'orthopaedic-materials/DF-001.png',
    ],
  },
  {
    key: 'bottom-covers',
    label: 'Bottom covers',
    tagline: 'Carbotec and anti-slip EVA finishing layers.',
    description:
      'Durable bottom-cover materials in sheet and roll formats: carbon-fibre look options plus a tear-resistant anti-slip EVA for heel-area builds.',
    lineTypes: ['Bottom Cover'],
    tileImages: [
      'orthopaedic-materials/BC-001.png',
      'orthopaedic-materials/BC-002.png',
      'orthopaedic-materials/BC-003.png',
    ],
  },
  {
    key: 'modification-accessories',
    label: 'Modification accessories',
    tagline: 'Metatarsal bars, domes, offloading pads, posting EVA.',
    description:
      'Ready-to-glue components plus posting EVA sheets in five thicknesses. Bag formats keep clinic stock organised for fast bespoke modifications.',
    lineTypes: ['Modification Accessory'],
    tileImages: [
      'orthopaedic-materials/MA-006.png',
      'orthopaedic-materials/MA-008.png',
      'orthopaedic-materials/MA-001.png',
      'orthopaedic-materials/MA-011.png',
    ],
  },
  {
    key: 'eva-blocks',
    label: 'EVA blocks',
    tagline: 'Wedge and rectangular blocks for milled-insole positives.',
    description:
      'Dual and tri-density EVA blocks plus a rigid PUR for the milled-insole fabrication process. Calibrated hardness for pronation, supination, and pediatric builds.',
    lineTypes: ['EVA Block'],
    tileImages: [
      'orthopaedic-materials/EB-005.png',
      'orthopaedic-materials/EB-001.png',
      'orthopaedic-materials/EB-006.png',
      'orthopaedic-materials/EB-011.png',
    ],
  },
  {
    key: 'other-materials',
    label: 'Other materials',
    tagline: 'Impression boxes and ancillary lab supplies.',
    description:
      'Foam impression boxes and other ancillary materials used to take patient impressions or finish custom builds. Sold per unit.',
    lineTypes: ['Other Materials'],
    // Use the foam-box product photo as the family tile image — it's
    // the hero product in this category. Marketing card uses the
    // per-product image (which falls back to the Package icon when
    // the file is missing).
    tileImages: [
      'orthopaedic-materials/foam-box.png',
    ],
  },
  {
    key: 'footwear',
    label: 'Therapeutic & recovery footwear',
    tagline: 'Fito by Wellwalking: therapeutic, post-op, recovery.',
    description:
      'Eleven footwear models built for diabetic foot, post-operative recovery, and rehabilitation. Adaptive uppers, biomechanical soles, ambidextrous post-op shoes.',
    lineTypes: ['Therapeutic Footwear', 'Post-Operative Footwear', 'Recovery Footwear'],
    tileImages: [
      'fito-shoes/F1BK.jpg',
      'fito-shoes/FFGY.jpg',
      'fito-shoes/FHBK.jpg',
      'fito-shoes/FWAN.jpg',
    ],
  },
];

/** Quick lookup: line_type → its parent FamilyGroup. */
export const LINE_TYPE_TO_FAMILY: Map<string, FamilyGroup> = (() => {
  const m = new Map<string, FamilyGroup>();
  for (const fg of FAMILY_GROUPS) {
    for (const lt of fg.lineTypes) m.set(lt, fg);
  }
  return m;
})();

/** All card codes that can ever appear in this family. Used by the
 *  marketing catalog page to filter the union of all card catalogs. */
export function familyContainsCard(family: FamilyGroup, lineType: string): boolean {
  return family.lineTypes.includes(lineType);
}
